import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { Command } from "cmdk";

import { Cross as CrossIcon } from "@/assets/icons/24/Cross";
import { Search as SearchIcon } from "@/assets/icons/24/Search";
import { cn } from "@/common/utils/css.utils";
import {
  SearchBoxContextProvider,
  useSearchBoxContext,
} from "@/features/search/components/search-box/search-box.context";
import { useSearchSubmit } from "@/features/search/hooks/useSearchSubmit";
import { ThreeDotBounce } from "@/ui/three-dot-bounce";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@radix-ui/react-popover";

/*
 * Combobox primitives for the search box.
 *
 * Radix ships no combobox primitive, so the parts come from two places: `@radix-ui/react-popover`
 * positions and dismisses the panel, and `cmdk` (itself built on Radix) supplies the combobox
 * semantics — role="combobox"/"listbox"/"option", `aria-activedescendant` and roving keyboard
 * navigation. Filtering and ranking are the search service's job, hence `shouldFilter={false}`.
 *
 * `Command` wraps both the input and the panel, and the panel is deliberately NOT portalled so
 * cmdk's list stays inside its own DOM tree.
 * ponytail: if a clipping ancestor ever appears, re-add `PopoverPortal` and re-check keyboard nav.
 */

/* Marks the input shell, so the panel can tell "clicked my own field" from "clicked outside". */
const SHELL_ATTRIBUTE = "data-search-box-shell";

type RootProps = { children: ReactNode; className?: string };

const Root = ({ children, className }: RootProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { submit, isSubmitting } = useSearchSubmit();

  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);

  /* Follow the results page's query, so the field shows what is actually being searched. */
  const routeTerm = router.query.query;
  useEffect(() => {
    setTerm(typeof routeTerm === "string" ? routeTerm : "");
  }, [routeTerm]);

  const submitAndClose = useCallback(
    (searchTerm: string) => {
      setOpen(false);
      submit(searchTerm);
    },
    [submit]
  );

  const value = useMemo(
    () => ({
      term,
      setTerm,
      open,
      setOpen,
      submit: submitAndClose,
      isSubmitting,
    }),
    [term, open, submitAndClose, isSubmitting]
  );

  return (
    <SearchBoxContextProvider value={value}>
      <Command
        label={t("header:nav-search-placeholder")}
        shouldFilter={false}
        loop
        className={cn("relative flex-1", className)}
      >
        <Popover open={open} onOpenChange={setOpen}>
          {children}
        </Popover>
      </Command>
    </SearchBoxContextProvider>
  );
};

/**
 * The field. `rightElement` is a slot, so what sits next to the input (search by image, …) can be
 * added without this component knowing about it.
 */
const Input = ({ rightElement }: { rightElement?: ReactNode }) => {
  const { t } = useTranslation();
  const { term, setTerm, open, setOpen, isSubmitting } = useSearchBoxContext();

  return (
    <PopoverAnchor asChild>
      <div
        {...{ [SHELL_ATTRIBUTE]: "" }}
        className={cn(
          "flex h-12 items-center space-x-2 rounded-full border bg-white px-4 transition-all",
          /*
           * The field sits on a white header, so it stands out by being raised rather than filled —
           * a grey fill is this codebase's disabled state (see `ui/cva/input/Input.tsx`). The tinted
           * resting border makes rest → hover → focus one progression instead of a jump from neutral
           * grey to dark green.
           */
          "border-brand-200 shadow-sm hover:border-brand-500",
          "focus-within:border-brand-500 focus-within:shadow-md focus-within:ring-2 focus-within:ring-brand-100"
        )}
      >
        {isSubmitting ? (
          <ThreeDotBounce className="size-5 shrink-0 text-brand-500" />
        ) : (
          <SearchIcon className="size-5 shrink-0 text-grey-500" />
        )}

        <Command.Input
          value={term}
          /* Typing reopens the panel. Only `focus` did before, which left it shut after a search:
           * submitting closes it but the field keeps the cursor, so editing the query had no focus
           * event to reopen on. cmdk fires this for real input only, so the URL prefill and the
           * clear button still set the term without popping the panel open. */
          onValueChange={value => {
            setTerm(value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          /* Rows keep focus in the field on mousedown, so a blur means the user really left. */
          onBlur={() => setOpen(false)}
          /* Radix dismisses on Escape only while its layer is the top one, and the header sits under
           * other layers (mega menu, tooltips). Closing here doesn't depend on that stack — and it
           * keeps the term and the focus, which a Radix dismiss would not. */
          onKeyDown={event => {
            if (event.key === "Escape" && open) {
              event.preventDefault();
              setOpen(false);
            }
          }}
          placeholder={t("header:nav-search-placeholder")}
          enterKeyHint="search"
          className="min-w-0 flex-1 bg-transparent font-body text-base text-black placeholder:text-grey-500 focus:outline-none"
        />

        {!!term && (
          <button
            type="button"
            aria-label={t("common:common-reset")}
            /* Keep focus in the field, so clearing does not close the panel. */
            onMouseDown={event => event.preventDefault()}
            onClick={() => setTerm("")}
            className="shrink-0 text-grey-500 transition-colors hover:text-black"
          >
            <CrossIcon className="size-5" />
          </button>
        )}

        {rightElement}
      </div>
    </PopoverAnchor>
  );
};

/** The dropdown. Sections are composed in as children. */
const Panel = ({ children }: { children: ReactNode }) => (
  <PopoverContent
    align="start"
    sideOffset={8}
    /* Focus stays in the field, both when the panel opens and after it closes. */
    onOpenAutoFocus={event => event.preventDefault()}
    onCloseAutoFocus={event => event.preventDefault()}
    onInteractOutside={event => {
      /* The field is outside the panel, so without this, clicking back into your own search box
       * would dismiss it. */
      if (
        event.target instanceof Element &&
        event.target.closest(`[${SHELL_ATTRIBUTE}]`)
      ) {
        event.preventDefault();
      }
    }}
    className="z-[200] w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-2xl border border-grey-200 bg-white p-2 shadow-lg"
  >
    {/* Native scroll rather than the styled ScrollArea: its viewport wrapper breaks cmdk's
        scroll-the-selection-into-view. */}
    <Command.List className="max-h-[22rem] overflow-y-auto overscroll-contain">
      {children}
    </Command.List>
  </PopoverContent>
);

/** Rendered by cmdk only when no section produced a row. */
const Empty = () => {
  const { t } = useTranslation();

  return (
    <Command.Empty className="px-3 py-2 font-body text-sm text-grey-600">
      {t("common:common-no-suggestions")}
    </Command.Empty>
  );
};

type ItemProps = {
  /** Unique across the whole list, so sections prefix it (`recent:…`, `suggestion:…`). */
  value: string;
  /** The query this row runs. */
  term: string;
  icon: ReactNode;
  /** Row label. Defaults to the term; sections pass a node to style the text differently. */
  children?: ReactNode;
};

const Item = ({ value, term, icon, children }: ItemProps) => {
  const { submit } = useSearchBoxContext();

  return (
    <Command.Item
      value={value}
      onSelect={() => submit(term)}
      /* Don't let a click on a row blur the field — that would close the panel before the select. */
      onMouseDown={event => event.preventDefault()}
      /* Rounded, because the panel's own padding insets the highlight from its edges. */
      className="flex cursor-pointer items-center space-x-3 rounded-lg px-3 py-1.5 font-body text-base text-black data-[selected=true]:bg-brand-50"
    >
      <span className="shrink-0 text-grey-500">{icon}</span>
      <span className="truncate">{children ?? term}</span>
    </Command.Item>
  );
};

/**
 * "Search for exactly what I typed", as the first row.
 *
 * cmdk always keeps a row selected and defaults to the first one, so putting this at the top makes
 * Enter mean what people expect — search the typed text — while ↓ moves into the suggestions. That
 * is cheaper and steadier than fighting the library to have nothing selected.
 */
const Term = () => {
  const { term } = useSearchBoxContext();
  const trimmed = term.trim();

  if (!trimmed) {
    return null;
  }

  return (
    <Item
      value={`term:${trimmed}`}
      term={trimmed}
      icon={<SearchIcon className="size-5" />}
    />
  );
};

export const SearchBox = { Root, Input, Panel, Term, Item, Empty };
